// FILE: assets/bold-sc-adapter.js
(function () {
  const CONST = { GLOBAL_TIMEOUT: 10000, CONTENT_TIMEOUT: 4000 };
  const SELECTOR = '[data-product-form], product-form';

  const waitForGlobal = (path, ms) => new Promise(resolve => {
    const start = Date.now();
    const parts = path.split('.');
    const check = () => {
      let cur = window;
      for (let p of parts) { cur = cur ? cur[p] : undefined; }
      if (cur !== undefined) return resolve(cur);
      if (Date.now() - start > ms) return resolve(null);
      requestAnimationFrame(check);
    };
    check();
  });

  const waitForContent = (ms) => new Promise(resolve => {
    if (document.querySelector('.bold_option')) return resolve(true);
    const obs = new MutationObserver((mutations, observer) => {
      if (document.querySelector('.bold_option')) {
        observer.disconnect();
        resolve(true);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => { obs.disconnect(); resolve(false); }, ms);
  });

  const waitForElement = (sel, ms) => new Promise(resolve => {
    if (document.querySelector(sel)) return resolve(document.querySelector(sel));
    const obs = new MutationObserver(() => {
      const el = document.querySelector(sel);
      if (el) { obs.disconnect(); resolve(el); }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => { obs.disconnect(); resolve(document.querySelector(sel)); }, ms);
  });

  if (customElements.get('bold-sc-adapter')) return;

  class BoldScAdapter extends HTMLElement {
    constructor() {
      super();
      this.state = { state: 'waiting', options: 'waiting', pre: 'waiting' };
      this.capabilities = { 
          nativeOptions: false,
          nativePricing: false,
          nativeEvents: false
      };
      this._resolvers = { ready: [], options: [], pre: [] };

      document.addEventListener('hub:ui:reflow_needed', () => {
          this._enhanceSwatches(); 
          this._groupOptionRows();

          setTimeout(() => {
              this._enhanceSwatches();
              this._groupOptionRows();
          }, 150);
      });
    }

    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      waitForElement(SELECTOR, CONST.GLOBAL_TIMEOUT).then(el => el?.setAttribute('data-hub-form', '1'));
      
      this._bindGlobalEvents();
      this._detect();

      // Listen for Bold's internal 'option_changed' or UI reflows
      document.addEventListener('hub:bold:option_changed', () => {
          setTimeout(() => this._enhanceSwatches(), 100);
      });

      // Global Mutation Observer to catch Bold's silent DOM replacements
      const observer = new MutationObserver((mutations) => {
          if (document.querySelector('.bold_option_swatch:not(:has(.selection-utility-bar))')) {
              this._enhanceSwatches();
          }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    getStatus() { return this.state; }
    getCapabilities() { return this.capabilities; } 
    
    whenReady(cb) { this._queue('ready', cb); }
    whenOptionsReady(cb) { this._queue('options', cb); }
    
    _queue(key, cb) {
      if (typeof cb !== 'function') return;
      const isReady = key === 'ready' ? this.state.state === 'ready' : this.state[key] === 'ready';
      if (isReady) cb(key === 'ready' ? this.getStatus() : window.BOLD);
      else this._resolvers[key].push(cb);
    }

    _emit(topic, detail) {
      this.dispatchEvent(new CustomEvent(topic, { detail, bubbles: true, composed: true }));
    }

    _revealUI() {
      const loader = document.querySelector('.loading-progress');
      if (loader) loader.remove();

      const picker = document.querySelector('bold-quantity-picker');
      if (picker) picker.classList.remove('bold-initial-hide');
      
      const allHidden = document.querySelectorAll('.bold_options--hidden');
      allHidden.forEach(el => {
          el.classList.remove('bold_options--hidden');
          if (el.classList.contains('bold_options')) {
              el.classList.add('bold_options_loaded');
          }
      });

      // Wrap the Bold options section in a collapsible disclosure
      this._wrapOptionsCollapsible();

      // NEW: Enhance the swatches once they are revealed
      this._enhanceSwatches();

      // Group option rows per declarative rules
      this._groupOptionRows();

      // Autofill Greek Letter dropdowns and text inputs from session storage
      this._autofillTextInputs();

      // Kick optional UI enhancers after options become visible.
      // (Safe no-op if the enhancer script isn't present.)
      try {
        if (window.BoldFileUploadUI && typeof window.BoldFileUploadUI.scan === 'function') {
          window.BoldFileUploadUI.scan();
        }
      } catch (e) {
        // no-op
      }
    }

    _groupOptionRows() {
      // Detect package products by the second Size option.
      const isPackage = !!(
        document.querySelector('[name="properties[Size #2]"]') ||
        document.querySelector('[name="properties[Size 2]"]')
      );

      const allTitles = [
        ...document.querySelectorAll(
          '.bold_option:not(.bold_option_hidden) .bold_option_title, ' +
          '.bold_option:not(.bold_option_hidden) label, ' +
          '.bold_option:not(.bold_option_hidden) .form__label'
        )
      ]
        .map(el =>
          el.textContent
            .replace(/\*/g, '')
            .trim()
            .toLowerCase()
        );

      const titleStr = allTitles.join('|');

      const RULES = [
        {
          match: /^greek letter\s*\d/i,
          cols: 3,
          minCount: 2,
        },

        {
          match: /letter color|border color/i,
          cols: 2,
          minCount: 2,
          condition: () =>
            titleStr.includes('letter color') &&
            titleStr.includes('border color'),
          optionMatch: (el) =>
            el.classList.contains('bold_option_dropdown'),
        },

        {
          /*
          * Package garment + size.
          *
          * Product 1:
          * T-Shirt Color
          * <bold-quantity-picker>  -> treated as Size
          *
          * Product 2:
          * T-Shirt Color #2
          * Size #2
          */
          match: /size(\s?#?\d?)?|tanktop color|tank top color|shirt color|sweatpants color|t-shirt color|crewneck color|hoodie color|sweatshirt color|long-sleeve color|long sleeve color|garment color/i,
          cols: 2,
          minCount: 2,
          packageOnly: true,
          condition: () => isPackage,
        },
      ];

      /*
      * Return the logical title for either a normal Bold option
      * or our Product 1 quantity picker.
      */
      const getOptionTitle = (opt) => {
        if (opt.matches('bold-quantity-picker')) {
          return 'Size';
        }

        return (
          opt.querySelector(
            '.bold_option_title, label, .form__label'
          )?.textContent || ''
        )
          .replace(/\*/g, '')
          .trim();
      };

      /*
      * Include:
      * - normal visible Bold options
      * - bold-quantity-picker for Product 1
      *
      * Exclude:
      * - hidden original Size
      * - descriptive/display headers
      * - anything already grouped
      */
      const allOptions = [
        ...document.querySelectorAll(
          '.bold_option:not(.bold_option_hidden), bold-quantity-picker'
        )
      ].filter(opt => {
        if (opt.closest('.bold-option-row')) return false;

        if (
          opt.classList &&
          opt.classList.contains('bold_option_displaytext')
        ) {
          return false;
        }

        return true;
      });

      if (allOptions.length === 0) return;

      // Group by parent so only actual siblings can share a row.
      const byParent = new Map();

      allOptions.forEach(opt => {
        const parent = opt.parentElement;
        if (!parent) return;

        if (!byParent.has(parent)) {
          byParent.set(parent, []);
        }

        byParent.get(parent).push(opt);
      });

      byParent.forEach((options) => {
        let i = 0;

        while (i < options.length) {
          const opt = options[i];
          const title = getOptionTitle(opt);

          const rule = RULES.find(r => {
            if (r.packageOnly && !isPackage) return false;
            if (r.condition && !r.condition()) return false;
            if (!r.match.test(title)) return false;
            if (r.optionMatch && !r.optionMatch(opt)) return false;

            return true;
          });

          if (!rule) {
            i++;
            continue;
          }

          const group = [opt];

          for (
            let j = i + 1;
            j < options.length && group.length < rule.cols;
            j++
          ) {
            const nextOpt = options[j];
            const nextTitle = getOptionTitle(nextOpt);

            /*
            * Make sure the next item satisfies the same rule,
            * including optionMatch where applicable.
            */
            const matches =
              rule.match.test(nextTitle) &&
              (!rule.optionMatch || rule.optionMatch(nextOpt));

            if (matches) {
              group.push(nextOpt);
            } else {
              break;
            }
          }

          if (group.length >= (rule.minCount || 2)) {
            const wrapper = document.createElement('div');
            wrapper.className = 'bold-option-row';
            wrapper.dataset.cols = group.length;

            opt.parentElement.insertBefore(wrapper, opt);

            group.forEach(item => {
              wrapper.appendChild(item);
            });

            i += group.length;
          } else {
            i++;
          }
        }
      });
    }

    _wrapOptionsCollapsible() {
      if (!document.querySelector('letter-generator')) return;
      const boldOptions = document.querySelector('.bold_options_loaded, .bold_options');
      if (!boldOptions || boldOptions.closest('.bold-collapsible')) return;

      // Build the <details> wrapper
      const details = document.createElement('details');
      details.className = 'bold-collapsible';

      const summary = document.createElement('summary');
      summary.className = 'bold-collapsible__summary';
      summary.innerHTML = `
        <span class="bold-collapsible__label">Enter options manually</span>
        <span class="bold-collapsible__selections"></span>
        <span class="bold-collapsible__chevron" aria-hidden="true"></span>
      `;

      details.appendChild(summary);

      details.appendChild(summary);
      boldOptions.parentElement.insertBefore(details, boldOptions);
      details.appendChild(boldOptions);

      // Update the summary selection preview whenever an option changes
      const selectionsEl = summary.querySelector('.bold-collapsible__selections');

      const updateSelections = () => {
        const facade = this.facade;
        if (!facade || !facade.options || !facade.options.getOptionSets) return;

        try {
          const selections = (facade.state && facade.state.getSelection && facade.state.getSelection()) || {};
          const defs = facade.options.getOptionSets() || [];

          const parts = defs
            .filter(d => {
              const key = (d.prettyName || d.name || '').replace(/\*/g, '').trim();
              const val = selections[key] !== undefined ? selections[key]
                : selections[Object.keys(selections).find(k =>
                    k.replace(/\*/g, '').trim().toLowerCase() === key.toLowerCase()
                  )];
              return val && val !== '' && val !== 'Unchecked';
            })
            .slice(0, 4)
            .map(d => {
              const key = (d.prettyName || d.name || '').replace(/\*/g, '').trim();
              const found = Object.keys(selections).find(k =>
                k.replace(/\*/g, '').trim().toLowerCase() === key.toLowerCase()
              );
              const val = (selections[key] !== undefined ? selections[key] : selections[found]) || '';
              const clean = String(val)
                .replace(/\s*\[.*?\]/g, '')
                .replace(/\s*\(\s*[\+\$][^)]*\)/g, '')
                .trim();
              return clean;
            })
            .filter(Boolean);

          selectionsEl.textContent = parts.length > 0 ? parts.join(' \u00B7 ') : '';
        } catch (e) {
          selectionsEl.textContent = '';
        }
      };

      document.addEventListener('hub:option:selection_changed', updateSelections);
      document.addEventListener('hub:options:ready', updateSelections);
    }

    _autofillTextInputs() {
      const sessionKeys = { 1: 'ltrgen-greek-1', 2: 'ltrgen-greek-2', 3: 'ltrgen-greek-3' };

      const formatLetter = (raw) => {
        if (!raw || raw === '') return '';
        const s = raw.toLowerCase();
        if (s === '*not applicable') return '';
        if (s === 'upsilon - ram head' || s === "upsilon - ram's head") return 'Upsilon';
        if (s === 'sigma alpha epsilon pi') return 'Sigma Alpha Epsilon Pi';
        if (s === 'fiji' || s === 'fiji letters') return 'FIJI';
        return s.charAt(0).toUpperCase() + s.slice(1);
      };

      const letters = [1, 2, 3].map(i => formatLetter(sessionStorage.getItem(sessionKeys[i])));
      const hasAny = letters.some(l => l !== '');
      if (!hasAny) return;

      // ── Greek Letter 1/2/3 dropdowns ──────────────────────────────────────
      // Use the facade's changeOption so the store stays in sync
      const facade = this.facade;
      if (facade && facade.options && facade.options.changeOption) {
        [1, 2, 3].forEach(function(num, i) {
          if (!letters[i]) return;
          facade.options.changeOption('Greek Letter ' + num, letters[i]);
        });
      }

      // ── "Enter Organization" free-text input ──────────────────────────────
      // Combines the three letter values into a formatted org name string
      const orgValue = letters.filter(Boolean).join(' ');
      const allOptions = document.querySelectorAll('.bold_option');
      allOptions.forEach(opt => {
        const titleEl = opt.querySelector('.bold_option_title, label, .form__label');
        const title = (titleEl ? titleEl.textContent : '').toLowerCase();
        if (!title.includes('enter organization') && !title.includes('organization name')) return;

        const input = opt.querySelector('input[type="text"], textarea');
        if (input && !input.value) {
          input.value = orgValue;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    _enhanceSwatches() {
      const swatchOptions = document.querySelectorAll('.bold_option_swatch');
      
      swatchOptions.forEach(option => {
        const elementWrap = option.querySelector('.bold_option_element');
        if (!elementWrap) return;

        // 1. FILTER FOR VISIBILITY (Ignore Bold's hidden conditional logic)
        const visibleValues = Array.from(option.querySelectorAll('.bold_option_value')).filter(v => {
            const style = window.getComputedStyle(v);
            return style.display !== 'none' && v.offsetWidth > 0 && v.offsetHeight > 0;
        });

        if (visibleValues.length === 0) return; 

        // 2. DYNAMIC DENSITY (Use classes to prevent Bold's JS overrides)
        const isLarge = visibleValues.length > 15; 
        option.classList.toggle('is-large-gallery', isLarge);

        // 3. SMART THRESHOLDS (14 for colors, 15 for designs)
        const threshold = isLarge ? 14 : 15; 

        if (isLarge) {
            option.style.setProperty('--swatch-width', '85px'); 
            option.style.setProperty('--swatch-size', '40px');
        } else {
            option.style.setProperty('--swatch-width', '110px');
            option.style.setProperty('--swatch-size', '64px');
        }

        // 4. PERSISTENT UI SYNC
        this._setupSelectionBars(option);

        // 5. EXPANSION GUARD
        if (visibleValues.length > threshold) {
            this._setupExpansion(option, threshold);
        } else {
            option.querySelector('.swatch-expand-btn')?.remove();
            elementWrap.style.maxHeight = 'none';
        }
      });
    }

    _setupSelectionBars(container) {
      if (container.querySelector('.selection-utility-bar')) return;

      const title = container.querySelector('.bold_option_title');
      if (!title) return;

      const bar = document.createElement('div');
      bar.className = 'selection-utility-bar';
      bar.setAttribute('data-field', title.textContent.replace('*', '').trim());

      bar.innerHTML = `
        <span class="selection-utility-label"></span>
        <div class="selection-utility-actions">
          <button type="button" class="utility-btn btn-zoom" title="View Detail">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </button>
          <button type="button" class="utility-btn btn-clear" title="Clear Selection">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
      `;

      // ROBUST SYNC LOGIC (Pulled from your updateOption snippet)
      const syncSelection = (input) => {
          if (!input) return;

          // If the input is unchecked, we treat it as 'Unselected'
          const value = input.checked ? input.value : null;
          
          const isUnselected =
            value === null ||
            value === '' ||
            value === 'Unchecked' ||
            (Array.isArray(value) && value.length === 0);

          if (isUnselected) {
              // LOGIC PATH: CLEAR
              this._updateSelectionBar({
                  field: bar.getAttribute('data-field'),
                  value: null,
                  image: null
              });
          } else {
              // LOGIC PATH: UPDATE
              const valContainer = input.closest('.bold_option_value');
              const swatchSpan = valContainer?.querySelector('.bold_option_value_swatch span');
              const bgImg = swatchSpan ? window.getComputedStyle(swatchSpan).backgroundImage : '';
              const cleanSrc = bgImg.replace(/url\(['"]?(.*?)['"]?\)/i, '$1');

              this._updateSelectionBar({
                  field: bar.getAttribute('data-field'),
                  value: value,
                  image: cleanSrc !== 'none' ? cleanSrc : null
              });
          }
      };

      // EVENT CAPTURE: Detect manual clicks/toggles on swatches
      container.addEventListener('change', (e) => {
          if (e.target.tagName === 'INPUT') syncSelection(e.target);
      }, { capture: true });

      // Action: Clear via "X" Button
      bar.querySelector('.btn-clear').onclick = (e) => {
          e.preventDefault();
          const activeInput = container.querySelector('input:checked');
          if (activeInput) {
              activeInput.checked = false;
              // Dispatching the event triggers the 'change' listener above
              activeInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
      };

      // Action: Zoom Button
      bar.querySelector('.btn-zoom').onclick = (e) => {
          e.preventDefault();
          const imgSrc = bar.getAttribute('data-current-img');
          const label = `${bar.getAttribute('data-field')}: ${bar.querySelector('.selection-utility-label').textContent}`;
          if (imgSrc) document.dispatchEvent(new CustomEvent('hub:ui:open_zoom', { detail: { src: imgSrc, label } }));
      };

      title.style.display = 'inline-block';
      title.parentNode.insertBefore(bar, title.nextSibling);

      // Initial Sync: Handle pre-selected defaults or conditional reveals
      const defaultChecked = container.querySelector('input:checked');
      if (defaultChecked) syncSelection(defaultChecked);
    }

    _updateSelectionBar(detail) {
      const { field, value, image } = detail;
      if (!field) return;

      const cleanField = field.replace(/\*/g, '').trim();
      const bar = document.querySelector(`.selection-utility-bar[data-field="${cleanField}"]`);

      if (!bar) return;

      // REFINED CLEAR CHECK (Consistent with your updateOption function)
      const isCleared = 
          value === null || 
          value === '' || 
          value === 'Unchecked' || 
          (Array.isArray(value) && value.length === 0);

      if (isCleared) {
        bar.classList.remove('is-visible'); 
        bar.setAttribute('data-current-img', '');
        bar.querySelector('.selection-utility-label').textContent = '';
      } else {
        bar.classList.add('is-visible'); 
        bar.querySelector('.selection-utility-label').textContent = value;
        if (image) bar.setAttribute('data-current-img', image);
      }
    }

    _setupExpansion(container, threshold) {
      const elementWrap = container.querySelector('.bold_option_element');
      // Prevent duplicate buttons if this is called multiple times
      if (!elementWrap || container.querySelector('.swatch-expand-btn')) return;

      elementWrap.style.overflow = 'hidden'; 

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch-expand-btn';
      
      const totalValues = elementWrap.querySelectorAll('.bold_option_value').length;
      const hiddenCount = totalValues - threshold;
      btn.innerText = `Show More (+${hiddenCount})`;

      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Stop Bold from intercepting this click
        const isExpanded = elementWrap.classList.toggle('is-expanded');
        btn.innerText = isExpanded ? 'Show Less' : `Show More (+${hiddenCount})`;
        
        if (!isExpanded) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      container.appendChild(btn);
    }

    _updateStatus(key, status) {
      if (this.state[key] === 'ready' && status !== 'ready') return;

      this.state[key] = status;
      this.setAttribute(key, status);
      
      if (status === 'ready') {
        const payload = key === 'options' ? { bold: window.BOLD, caps: this.capabilities } : { pre: window.BOLD?.pre };
        this._emit(`hub:${key}:ready`, payload);
        this._resolvers[key].forEach(cb => cb(payload));
        this._resolvers[key] = [];
        
        if (key === 'options') this._revealUI();
      }
      
      if (this.state.options === 'ready' || this.state.pre === 'ready') {
        this.state.state = 'ready';
        this.setAttribute('state', 'ready');
        this._resolvers.ready.forEach(cb => cb(this.getStatus()));
        this._resolvers.ready = [];
      }
    }

    _bindGlobalEvents() {
        // Bold's common module may not be loaded yet when this fires.
        // Retry until it's available or until options are already ready.
        const tryBind = (attempt = 0) => {
            if (this.state.options === 'ready') return;
            if (window.BOLD && window.BOLD.common && window.BOLD.common.eventEmitter) {
                window.BOLD.common.eventEmitter.on('BOLD_OPTIONS_no_option_products_loaded', () => {
                    // console.log("[Bold Adapter] Fast Fail: No Options detected.");
                    this._updateStatus('options', 'ready'); 
                });
            } else if (attempt < 20) {
                setTimeout(() => tryBind(attempt + 1), 250);
            }
        };
        tryBind();
    }

    async _detect() {
      const bold = await waitForGlobal('BOLD', CONST.GLOBAL_TIMEOUT);
      
      if (!bold) {
          console.warn("[Bold Adapter] Bold not found. Falling back to DOM mode.");
          this._updateStatus('options', 'absent');
          this._updateStatus('pre', 'absent');
          this._revealUI();
          return;
      }

      if (bold.options && bold.options.app) {
          this.capabilities.nativeOptions = typeof bold.options.app.getOptionProductByForm === 'function';
          this.capabilities.nativeEvents = !!bold.options.app.ee;
      }
      if (bold.common && bold.common.cartDoctor) {
          this.capabilities.nativePricing = true;
      }

      this._bindInternalEvents(bold);

      // console.log("[Bold Adapter] Capabilities Detected:", this.capabilities);

      if (this.state.options !== 'ready') {
          await waitForContent(CONST.CONTENT_TIMEOUT);
          const hasOpts = this.capabilities.nativeOptions || document.querySelector('.bold_option');
          this._updateStatus('options', hasOpts ? 'ready' : 'absent');
      }

      // Hard fallback — if options still haven't confirmed ready after a generous
      // window, force the reveal so the page doesn't stay permanently locked.
      setTimeout(() => {
        if (this.state.options !== 'ready') {
          console.warn('[Bold Adapter] Hard fallback: forcing options ready after timeout.');
          this._updateStatus('options', 'ready');
        }
      }, CONST.CONTENT_TIMEOUT + 2000);

      // --- NEW: Robust Wait for Price Rules (PRE) ---
      // Often BOLD exists, but BOLD.pre loads 1-2 seconds later via bold-pr.js
      const pre = await waitForGlobal('BOLD.pre', 6000);
      if (pre) {
          if (typeof pre.ready === 'function') {
              try { 
                  await pre.ready(); 
                  // console.log("[Bold Adapter] Price Rules Ready (Memory Mode Available)");
                  this._updateStatus('pre', 'ready'); 
              }
              catch (e) { 
                  console.warn("[Bold Adapter] PRE ready() failed", e);
                  this._updateStatus('pre', 'ready'); 
              } 
          } else {
              this._updateStatus('pre', 'ready');
          }
      } else {
          // console.log("[Bold Adapter] Price Rules not detected.");
          this._updateStatus('pre', 'absent');
      }
    }

    _bindInternalEvents(bold) {
        if (bold.common && bold.common.eventEmitter) {
            bold.common.eventEmitter.on('BOLD_COMMON_variant_changed', (data) => {
                this._emit('hub:bold:variant_changed', data);
            });
            bold.common.eventEmitter.on('BOLD_OPTIONS_no_option_products_loaded', () => {
                 if (this.state.options !== 'ready') {
                     // console.log("[Bold Adapter] Fast Fail (Late): No Options detected.");
                     this._updateStatus('options', 'ready');
                 }
            });
        }

        if (this.capabilities.nativeEvents && bold.options.app.ee) {
            bold.options.app.ee.on('option_changed', (data) => {
                this._emit('hub:bold:option_changed', data);
            });
        }
    }
  }

  customElements.define('bold-sc-adapter', BoldScAdapter);
  
  if (!document.querySelector('bold-sc-adapter')) {
    const el = document.createElement('bold-sc-adapter');
    el.hidden = true;
    document.body ? document.body.appendChild(el) : document.addEventListener('DOMContentLoaded', () => document.body.appendChild(el));
  }

  // Quick-View Modal Logic (Renamed Prefix)
  // Native <dialog> Modal Logic with Labels
  document.addEventListener('hub:ui:open_zoom', (e) => {
      const { src, label } = e.detail; // Extract both image and text label
      if (!src) return;

      let modal = document.getElementById('bold-zoom-modal');
      
      if (!modal) {
          modal = document.createElement('dialog');
          modal.id = 'bold-zoom-modal';
          modal.innerHTML = `
              <div class="bold-modal-wrapper">
                  <button class="bold-modal-close" aria-label="Close" method="dialog">&times;</button>
                  <img src="" alt="High Res Preview">
                  <div class="bold-modal-caption"></div> </div>
          `;
          document.body.appendChild(modal);

          modal.addEventListener('click', (event) => {
              if (event.target === modal) modal.close();
          });

          modal.querySelector('.bold-modal-close').onclick = () => modal.close();
      }

      // Inject the data
      modal.querySelector('img').src = src;
      modal.querySelector('.bold-modal-caption').textContent = label || '';

      if (typeof modal.showModal === 'function') {
          modal.showModal();
      }
  });
})();